import React, { useState } from 'react'
import { ArrowLeft, Copy, Check, Download, RotateCcw, Zap, Info, Network, Server, ChevronDown, ChevronRight, BarChart3 } from 'lucide-react'
import { getSubnetInfo, getVcnInfo, planToText, planToJson, createDynamicTopologyDef, proposeCidrs } from '../utils/cidrCalculator'
import { parseCidr, formatNum, splitCidr, prefixToMask } from '../utils/cidrUtils'

function Step4Calculator({ result, onBack, onReset }) {
  const { topologyDef: initialTopology, plan: initialPlan, config } = result
  const [topologyDef, setTopologyDef] = useState(initialTopology)
  const [plan, setPlan] = useState(initialPlan)
  const [copiedKey, setCopiedKey] = useState(null)
  const [expandedVcns, setExpandedVcns] = useState(() =>
    new Array(config.vcnCount).fill(false)
  )
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [randomSeed, setRandomSeed] = useState(0)

  // Store original base CIDRs from Step 3 for consistent randomization
  const originalBaseCidrs = React.useMemo(() =>
    initialTopology.vcns.map(v => v.baseCidr || '10.0.0.0/16'),
    [initialTopology]
  )



  // Generate random CIDR plan with offset
  const handleProposeAll = () => {
    const newSeed = randomSeed + 1
    setRandomSeed(newSeed)

    // Create a new topology with random offsets using original base CIDRs
    const { topology: newTopology, plan: newPlan } = proposeRandomCidrs(
      topologyDef,
      newSeed,
      originalBaseCidrs
    )
    setTopologyDef(newTopology)
    setPlan(newPlan)
  }

  const toggleVcn = (vi) => {
    setExpandedVcns(prev => {
      const updated = [...prev]
      updated[vi] = !updated[vi]
      return updated
    })
  }

  const expandAll = () => {
    setExpandedVcns(new Array(config.vcnCount).fill(true))
  }

  const collapseAll = () => {
    setExpandedVcns(new Array(config.vcnCount).fill(false))
  }

  const scrollToVcn = (vi) => {
    setExpandedVcns(prev => {
      const wasExpanded = prev[vi]
      const updated = [...prev]
      updated[vi] = !wasExpanded

      if (!wasExpanded) {
        // Expanding - scroll to this VCN
        setTimeout(() => {
          const element = document.getElementById(`vcn-section-${vi}`)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 0)
      } else {
        // Collapsing - check if any VCNs are still expanded
        const anyExpanded = updated.some((v, i) => i !== vi && v)
        if (!anyExpanded) {
          // None expanded - scroll back to top
          setTimeout(() => {
            const container = document.querySelector('.app-main')
            if (container) {
              container.scrollTo({ top: 0, behavior: 'smooth' })
            }
          }, 0)
        }
      }

      return updated
    })
  }

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 2000)
    } catch (err) {
      // Fallback
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 2000)
    }
  }

  const handleCopyText = () => {
    const text = planToText(plan, topologyDef)
    copyToClipboard(text, 'text')
  }

  const handleCopyJson = () => {
    const json = planToJson(plan, topologyDef)
    copyToClipboard(json, 'json')
  }

  const handleDownload = () => {
    const text = planToText(plan, topologyDef)
    const json = planToJson(plan, topologyDef)

    const content = `OCI VCN CIDR Plan
Generated: ${new Date().toLocaleString()}

${'='.repeat(60)}
TEXT FORMAT
${'='.repeat(60)}

${text}

${'='.repeat(60)}
JSON FORMAT
${'='.repeat(60)}

${json}
`

    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `oci-cidr-plan-${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const totalSubnets = config.subnetCounts.reduce((a, b) => a + b, 0)

  return (
    <div className="step-card results-card">
      <div className="results-sticky-bar">
        <div className="step-header">
          <h2>Your CIDR Plan</h2>
          <p className="step-description">
            {config.vcnCount} VCN{config.vcnCount !== 1 ? 's' : ''} with {totalSubnets} subnet{totalSubnets !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="results-with-sidebar">
        {/* Sidebar Navigation - always fixed on left */}
        <div className="vcn-sidebar-fixed">
          <div className={`vcn-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <span className="sidebar-title">VCN Navigation</span>
          </div>

          {sidebarOpen && (
            <div className="sidebar-controls">
              <button className="sidebar-control-btn" onClick={expandAll}>
                <ChevronDown size={16} />
                Expand All
              </button>
              <button className="sidebar-control-btn" onClick={collapseAll}>
                <ChevronRight size={16} />
                Collapse All
              </button>
            </div>
          )}

          <div className="sidebar-content">
            {plan.map((vcnPlan, vi) => {
              const vcn = topologyDef.vcns[vi]
              const isExpanded = expandedVcns[vi]

              return (
                <button
                  key={vi}
                  className={`sidebar-item ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => scrollToVcn(vi)}
                >
                  <span className="sidebar-item-number">{vi + 1}</span>
                  {sidebarOpen && (
                    <div className="sidebar-item-info">
                      <span className="sidebar-item-name">{config.vcnNames[vi]}</span>
                      <span className="sidebar-item-cidr">{vcnPlan.vcnCidr || 'No CIDR'}</span>
                    </div>
                  )}
                </button>
              )
            })}
          </div>

          {sidebarOpen && (
            <div className="sidebar-actions-panel">
              <div className="sidebar-section-title">Actions</div>

              <button className="sidebar-action-btn-large btn-accent" onClick={handleProposeAll}>
                <Zap size={16} />
                Randomize Plan
              </button>

              <button
                className={`sidebar-action-btn-large ${copiedKey === 'text' ? 'copied' : ''}`}
                onClick={handleCopyText}
              >
                {copiedKey === 'text' ? <Check size={16} /> : <Copy size={16} />}
                Copy Text
              </button>

              <button
                className={`sidebar-action-btn-large ${copiedKey === 'json' ? 'copied' : ''}`}
                onClick={handleCopyJson}
              >
                {copiedKey === 'json' ? <Check size={16} /> : <Copy size={16} />}
                Copy JSON
              </button>

              <button className="sidebar-action-btn-large" onClick={handleDownload}>
                <Download size={16} />
                Download
              </button>

              <div className="sidebar-divider" />

              <button className="sidebar-action-btn-large" onClick={onBack}>
                <ArrowLeft size={16} />
                Back to Configuration
              </button>

              <button className="sidebar-action-btn-large btn-danger" onClick={onReset}>
                <RotateCcw size={16} />
                Start New Plan
              </button>
            </div>
          )}
        </div>
        </div>

        {/* Main Results Content */}
        <div className="results-content">
          {plan.map((vcnPlan, vi) => {
            const vcn = topologyDef.vcns[vi]
            const isExpanded = expandedVcns[vi]

            return (
              <div
                key={vi}
                id={`vcn-section-${vi}`}
                className={`vcn-result-card ${isExpanded ? 'expanded' : 'collapsed'}`}
              >
                <div
                  className="vcn-result-header"
                  onClick={() => toggleVcn(vi)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="vcn-title">
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <Network size={20} />
                    <div>
                      <span className="vcn-index">VCN {vi + 1}</span>
                      <span className="vcn-label">{config.vcnNames[vi]}</span>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                    <div className="vcn-result-body">
                    <div className="base-info-row">
                      <span className="base-label">Selected Base (Step 3):</span>
                      <span className="base-value">{config.baseCidrs[vi]}</span>
                    </div>
                    <div className="base-info-row">
                      <span className="base-label">Assigned Block:</span>
                      <span className="base-value">{topologyDef.vcns[vi]?.baseCidr || config.baseCidrs[vi]}</span>
                    </div>

                    <div className="vcn-cidr-row">
                      <label className="cidr-label">VCN CIDR (calculated)</label>
                      <div className="cidr-input-wrapper">
                        <input
                          type="text"
                          className="cidr-input cidr-readonly"
                          value={vcnPlan.vcnCidr}
                          readOnly
                          placeholder="e.g. 10.0.0.0/16"
                        />
                        <button
                          className={`btn-copy ${copiedKey === `vcn-${vi}` ? 'copied' : ''}`}
                          onClick={() => copyToClipboard(vcnPlan.vcnCidr, `vcn-${vi}`)}
                          title="Copy VCN CIDR"
                        >
                          {copiedKey === `vcn-${vi}` ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </div>
                    </div>

                    <VcnInfo vcnCidr={vcnPlan.vcnCidr} subnetCidrs={vcnPlan.subnetCidrs} utilizationStats={vcnPlan.utilizationStats} useFullBaseCidr={vcnPlan.useFullBaseCidr} />

                    {/* VLSM Warnings */}
                    {vcnPlan.vlsmWarnings && vcnPlan.vlsmWarnings.length > 0 && (
                      <div className="vlsm-warnings">
                        {vcnPlan.vlsmWarnings.map((warning, wi) => (
                          <div key={wi} className="vlsm-warning">
                            <Info size={14} />
                            <span>{warning}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {vcn.subnets.length > 0 && (
                      <div className="subnets-section">
                        <div className="subnets-divider">
                          <Server size={14} />
                          <span>Subnets ({vcn.subnets.length})</span>
                          {config.subnetHosts && (
                            <span className="vlsm-badge">VLSM Mode</span>
                          )}
                        </div>
                        <div className="subnets-list">
                          {vcn.subnets.map((subnet, si) => {
                            const subnetCidr = vcnPlan.subnetCidrs[si] || ''
                            const requiredHosts = config.subnetHosts?.[vi]?.[si]

                            return (
                              <SubnetRow
                                key={si}
                                index={si}
                                label={subnet.label}
                                cidr={subnetCidr}
                                requiredHosts={requiredHosts}
                                onCopy={() => copyToClipboard(subnetCidr, `subnet-${vi}-${si}`)}
                                copied={copiedKey === `subnet-${vi}-${si}`}
                              />
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

    </div>
  )
}

// Calculate minimum VCN prefix based on subnet count
// Using /24 for all subnets
function calculateMinVcnPrefix(subnetCount) {
  if (subnetCount <= 1) return 24  // /24 for single subnet
  if (subnetCount <= 2) return 23  // /23 fits 2 /24 subnets
  if (subnetCount <= 4) return 22  // /22 fits 4 /24 subnets
  if (subnetCount <= 8) return 21  // /21 fits 8 /24 subnets
  if (subnetCount <= 16) return 20  // /20 fits 16 /24 subnets
  if (subnetCount <= 32) return 19  // /19 fits 32 /24 subnets
  if (subnetCount <= 64) return 18  // /18 fits 64 /24 subnets
  if (subnetCount <= 128) return 17 // /17 fits 128 /24 subnets
  return 16  // /16 fits up to 256 /24 subnets
}

// Simple seeded random number generator
function seededRandom(seed) {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

// Shuffle array using seed
function seededShuffle(array, seed) {
  const result = [...array]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(seed + i) * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

// Random CIDR proposal function - uses original base CIDRs from Step 3
// and calculates appropriately-sized blocks based on subnet needs
function proposeRandomCidrs(topologyDef, seed, originalBaseCidrs) {
  const vcns = [...topologyDef.vcns]

  // Calculate minimum prefix needed for each VCN
  const vcnPrefixes = vcns.map(vcn => {
    const subnetCount = vcn.subnets?.length || 0
    return calculateMinVcnPrefix(subnetCount)
  })

  // Track assigned sized blocks to avoid overlaps
  const assignedBlocks = []

  // Helper to check if two CIDRs overlap
  function cidrsOverlap(cidr1, cidr2) {
    const p1 = parseCidr(cidr1)
    const p2 = parseCidr(cidr2)
    if (!p1 || !p2) return false
    return (p1.start <= p2.end && p1.end >= p2.start)
  }

  // Create new topology with appropriately-sized base assignments
  const randomizedVcns = vcns.map((vcn, i) => {
    const originalBase = originalBaseCidrs[i] || '10.0.0.0/16'
    const minPrefix = vcnPrefixes[i]

    // Parse the original base
    const baseParsed = parseCidr(originalBase)
    if (!baseParsed) return vcn

    // First try: generate candidates from WITHIN the original base CIDR
    // This respects the user's custom CIDR choice
    let sizedBlocks = splitCidr(originalBase, minPrefix, 1000)

    // If the original base is smaller than minPrefix, we can't split it
    // In that case, use the original base as-is
    if (sizedBlocks.length === 0) {
      // Check if original base itself doesn't overlap
      const overlaps = assignedBlocks.some(assigned => cidrsOverlap(originalBase, assigned))
      if (!overlaps) {
        assignedBlocks.push(originalBase)
        return {
          ...vcn,
          baseCidr: originalBase
        }
      }
      // If it overlaps, fall through to find an alternative
    } else {
      // Shuffle the sized blocks for randomization
      const shuffledSized = seededShuffle(sizedBlocks, seed + i * 500)

      // Find a sized block that doesn't overlap with assigned blocks
      for (const sizedBlock of shuffledSized) {
        const overlaps = assignedBlocks.some(assigned => cidrsOverlap(sizedBlock, assigned))
        if (!overlaps) {
          assignedBlocks.push(sizedBlock)
          return {
            ...vcn,
            baseCidr: sizedBlock
          }
        }
      }
    }

    // Second try: expand to the broader RFC1918 range if custom base is exhausted/overlaps
    // Generate all possible /16 blocks from the appropriate RFC1918 range
    const allCandidates = []

    if (baseParsed.start >= 0x0a000000 && baseParsed.end <= 0x0affffff) {
      // 10.0.0.0/8
      for (let b = 0; b < 256; b++) {
        allCandidates.push(`10.${b}.0.0/16`)
      }
    } else if (baseParsed.start >= 0xac100000 && baseParsed.end <= 0xac1fffff) {
      // 172.16.0.0/12
      for (let b = 16; b <= 31; b++) {
        allCandidates.push(`172.${b}.0.0/16`)
      }
    } else if (baseParsed.start >= 0xc0a80000 && baseParsed.end <= 0xc0a8ffff) {
      // 192.168.0.0/16
      allCandidates.push('192.168.0.0/16')
    }

    // Shuffle candidates based on seed for randomization
    const shuffledCandidates = seededShuffle(allCandidates, seed + i * 1000)

    // Try each candidate block
    let selectedSizedBlock = null

    for (const candidate16 of shuffledCandidates) {
      // Get all possible sized blocks from this /16
      const sizedBlocks = splitCidr(candidate16, minPrefix, 1000)
      if (sizedBlocks.length === 0) continue

      // Shuffle the sized blocks for more randomization
      const shuffledSized = seededShuffle(sizedBlocks, seed + i * 500)

      // Find a sized block that doesn't overlap with assigned blocks
      for (const sizedBlock of shuffledSized) {
        const overlaps = assignedBlocks.some(assigned => cidrsOverlap(sizedBlock, assigned))
        if (!overlaps) {
          selectedSizedBlock = sizedBlock
          assignedBlocks.push(selectedSizedBlock)
          break
        }
      }

      if (selectedSizedBlock) break
    }

    // Fallback to original base if nothing worked
    if (!selectedSizedBlock) {
      selectedSizedBlock = originalBase
      assignedBlocks.push(selectedSizedBlock)
    }

    return {
      ...vcn,
      baseCidr: selectedSizedBlock
    }
  })

  const randomizedTopology = {
    ...topologyDef,
    vcns: randomizedVcns
  }

  const result = proposeCidrs(randomizedTopology)
  return { topology: randomizedTopology, ...result }
}

function VcnInfo({ vcnCidr, subnetCidrs, utilizationStats, useFullBaseCidr }) {
  const p = parseCidr(vcnCidr)
  if (!p) return null

  const firstSubnet = subnetCidrs.find(s => s && parseCidr(s))
  const subnetPrefix = firstSubnet ? parseCidr(firstSubnet)?.prefix || 24 : 24
  const vcnInfo = getVcnInfo(vcnCidr, subnetPrefix)

  if (!vcnInfo) return null

  return (
    <div className="vcn-info-section">
      <div className="vcn-info-chips">
        <div className="info-chip">
          <strong>{formatNum(vcnInfo.totalIps)}</strong>
          <span>total IPs</span>
        </div>
        <div className="info-chip">
          <strong>{formatNum(vcnInfo.subnetsPossible)}</strong>
          <span>possible /{subnetPrefix} subnets</span>
        </div>
      </div>

      {useFullBaseCidr && utilizationStats && (
        <div className="utilization-stats-panel">
          <div className="utilization-header">
            <BarChart3 size={16} />
            <span>Base CIDR Utilization Statistics</span>
          </div>
          <div className="utilization-stats">
            <div className="utilization-stat">
              <span className="stat-label">Total Base IPs</span>
              <span className="stat-value">{formatNum(utilizationStats.totalBaseIps)}</span>
            </div>
            <div className="utilization-stat">
              <span className="stat-label">IPs Used by Subnets</span>
              <span className="stat-value used">{formatNum(utilizationStats.usedIps)}</span>
            </div>
            <div className="utilization-stat">
              <span className="stat-label">Wasted IPs</span>
              <span className="stat-value wasted">{formatNum(utilizationStats.wastedIps)}</span>
            </div>
            <div className="utilization-stat">
              <span className="stat-label">Utilization</span>
              <span className={`stat-value utilization ${parseFloat(utilizationStats.utilizationPercent) > 80 ? 'good' : parseFloat(utilizationStats.utilizationPercent) > 50 ? 'medium' : 'low'}`}>
                {utilizationStats.utilizationPercent}%
              </span>
            </div>
          </div>
          <div className="utilization-bar-container">
            <div className="utilization-bar">
              <div
                className="utilization-bar-fill"
                style={{ width: `${Math.min(utilizationStats.utilizationPercent, 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SubnetRow({ index, label, cidr, requiredHosts, onCopy, copied }) {
  const info = cidr ? getSubnetInfo(cidr) : null
  const isVlsm = requiredHosts !== undefined && requiredHosts !== null

  return (
    <div className="subnet-row">
      <div className="subnet-main">
        <div className="subnet-header-row">
          <span className="subnet-badge">Subnet {index + 1}</span>
          <span className="subnet-name">{label}</span>
          {isVlsm && (
            <span className="vlsm-host-badge">
              {requiredHosts} hosts
            </span>
          )}
        </div>

        <div className="cidr-input-wrapper">
          <input
            type="text"
            className="cidr-input cidr-readonly"
            value={cidr}
            readOnly
            placeholder="e.g. 10.0.0.0/24"
          />
          <button
            className={`btn-copy ${copied ? 'copied' : ''}`}
            onClick={onCopy}
            title="Copy subnet CIDR"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      </div>

      {info && (
        <div className="subnet-info-panel">
          <div className="info-stats">
            <div className="stat">
              <span className="stat-label">Total IPs</span>
              <span className="stat-value">{formatNum(info.total)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Usable</span>
              <span className={`stat-value ${isVlsm && info.usable < requiredHosts ? 'error' : 'highlight'}`}>
                {formatNum(info.usable)}
                {isVlsm && info.usable >= requiredHosts && ' ✓'}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Prefix</span>
              <span className="stat-value">/{info.prefix}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Mask</span>
              <span className="stat-value">{prefixToMask(info.prefix)}</span>
            </div>
          </div>

          {isVlsm && (
            <div className="vlsm-info-row">
              <span className="vlsm-required">Required: {requiredHosts} hosts</span>
              <span className={`vlsm-allocated ${info.usable >= requiredHosts ? 'sufficient' : 'insufficient'}`}>
                {info.usable >= requiredHosts
                  ? `Allocated: ${formatNum(info.usable)} hosts (sufficient)`
                  : `Allocated: ${formatNum(info.usable)} hosts (INSUFFICIENT!)`}
              </span>
            </div>
          )}

          <div className="address-layout">
            <div className="layout-title">Address Layout (OCI reserves 3 IPs)</div>
            <table className="layout-table">
              <tbody>
                <tr>
                  <td className="layout-badge-cell"><span className="layout-badge reserved">Reserved</span></td>
                  <td className="layout-ip-cell">{info.networkAddr}</td>
                  <td className="layout-desc-cell">Network address</td>
                </tr>
                <tr>
                  <td className="layout-badge-cell"><span className="layout-badge gateway">Gateway</span></td>
                  <td className="layout-ip-cell">{info.gatewayAddr}</td>
                  <td className="layout-desc-cell">VCN router (reserved)</td>
                </tr>
                <tr>
                  <td className="layout-badge-cell"><span className="layout-badge usable">Usable</span></td>
                  <td className="layout-ip-cell">{info.firstUsable} – {info.lastUsable}</td>
                  <td className="layout-desc-cell">{formatNum(info.usable)} assignable IPs</td>
                </tr>
                <tr>
                  <td className="layout-badge-cell"><span className="layout-badge reserved">Reserved</span></td>
                  <td className="layout-ip-cell">{info.lastAddr}</td>
                  <td className="layout-desc-cell">Last address (reserved)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default Step4Calculator
